import React, {RefObject, useRef} from 'react';
import cn from 'classnames';
import {ParentProvider} from 'contentful/components/NavigationItem/NavigationItem';

import {EEventType} from 'coreClasses/events/IEvents';
import ComponentErrorBoundary from 'coreComponents/ComponentErrorBoundary';
import {useUI} from 'coreHooks';
import {NavLink} from 'coreUtils/adapters/router';
import {getIsInternalUrl} from 'coreUtils/url';

import Button from 'brandComponents/Button';
import {useHeaderCtx} from 'brandComponents/Header/HeaderCtx';
import {useMainNavigationContext} from 'brandComponents/Header/Subcomponents/MainNavigation/MainNavigationContext';
import Icon from 'brandComponents/Icon';
import ShowOrHideOnBreakpoint from 'brandComponents/Responsive/ShowOrHideOnBreakpoint';
import {usePromotionViewEvent} from 'brandHooks/usePromotionViewEvent';
import {useTrackPromotionEvent} from 'brandHooks/useTrackPromotionEvent';

import {INavigationItemProps} from '../../INavigationItem';
import NavigationItemList from '../NavigationItemList';

type TNavItemLinkProps = Pick<
	INavigationItemProps,
	'url' | 'title' | 'color' | 'description' | 'navigationParams' | 'queryParameters'
> & {
	/** From `ShowOrHideOnBreakpoint` (`attachToChildren`). */
	className?: string;
	isEditorialItem: boolean;
	image: INavigationItemProps['image'];
	elementRef: RefObject<HTMLAnchorElement>;
	onNavigate: () => void;
};

// Module level: defined inside `NavigationItemContent` it was a new component type on every render
// (remounted each time, and not compilable by the React Compiler).
const NavItemLink = ({
	className = '',
	url,
	title,
	color,
	description,
	navigationParams,
	queryParameters,
	isEditorialItem,
	image,
	elementRef,
	onNavigate,
}: TNavItemLinkProps) => {
	const isServerUI = useUI('isServer');
	const isInternalUrl = url && getIsInternalUrl(url);

	const content = (
		<>
			{isEditorialItem && !isServerUI && (
				<ShowOrHideOnBreakpoint query="<xx-large">{image}</ShowOrHideOnBreakpoint>
			)}
			<span className="navigation-item-content__title">
				{title}
			</span>
			{!!description && (
				<span className="navigation-item-content__description">
					{description}
				</span>
			)}
		</>
	);

	if (isInternalUrl) {
		return (
			<ComponentErrorBoundary>
				<NavLink
					ref={elementRef}
					exact
					style={{color}}
					to={{pathname: url, state: navigationParams, search: queryParameters || ''}}
					className={cn(className, 'nav-link', {
						'nav-link-editorial': isEditorialItem,
						'nav-link-with-description': !!description,
					})}
					isActive={(match, location) => location.pathname === url}
					onClick={onNavigate}
				>
					{content}
				</NavLink>
			</ComponentErrorBoundary>
		);
	}

	const Tag = url ? 'a' : 'span';

	return (
		<Tag
			href={url}
			style={{color}}
			className={cn(className, 'nav-link', {
				'nav-link-editorial': isEditorialItem,
				'nav-link-with-description': !!description,
			})}
		>
			{content}
		</Tag>
	);
};

// This shows mega menu on desktop if exists or sub menu on mobile also if the mega menu exists
// If no mega menu it shows the sub menu on desktop and mobile
const NavigationItemContent = (props: INavigationItemProps) => {
	const headerCtx = useHeaderCtx(true);
	const mainNavigationCtx = useMainNavigationContext(true);
	const elementRef = useRef(null);
	const {trackPromotionEvent} = useTrackPromotionEvent();

	const {
		description,
		url,
		title,
		color,
		template,
		isActive,
		hasActive,
		animationClasses,
		children,
		navigationParams,
		queryParameters,
		image,
		customSettings,
		id,
	} = props;

	const isEditorialItem = customSettings?.showImage && image;
	const urlWithQueryParams = `${url}?${queryParameters || ''}`;

	usePromotionViewEvent(elementRef, urlWithQueryParams);

	const onNavigate = () => {
		headerCtx?.handleNavigationVisibility();

		if (queryParameters) {
			trackPromotionEvent(urlWithQueryParams, EEventType.SELECT_BRAND_PROMOTION);
		}
	};
	const linkProps = {
		url,
		title,
		color,
		description,
		navigationParams,
		queryParameters,
		isEditorialItem: !!isEditorialItem,
		image,
		elementRef,
		onNavigate,
	};

	const onMouseEnter = () => {
		if (id && image) {
			mainNavigationCtx?.setActiveCategoryLabel(title);
			mainNavigationCtx?.setActiveLink(id);
		}
	};

	const onMouseLeave = () => {
		headerCtx?.setIsMegamenuHidden(false);

		if (mainNavigationCtx?.activeLink === id) {
			mainNavigationCtx?.setActiveLink(undefined);
		}
	};

	if (!title) {
		return null;
	}

	return (
		<li
			className={cn({
				'has-sub-list': !!children,
				'has-multi-sub-lists': !template && !!props.hasSubLists,
				active: isActive,
				editorial: isEditorialItem,
			})}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<ShowOrHideOnBreakpoint query="<xx-large" attachToChildren>
				{typeof props.handleNavigationItemClick === 'function' && !!children ? (
					<Button
						onClick={props.handleNavigationItemClick}
						variant="anything"
						classNames="navigation-trigger"
					>
						<span style={{color}}>{title}</span>
						<Icon name="arrow-right" width={12} height={12} />
					</Button>
				) : (
					<NavItemLink {...linkProps} />
				)}
			</ShowOrHideOnBreakpoint>

			<ShowOrHideOnBreakpoint query=">=xx-large" attachToChildren>
				<NavItemLink {...linkProps} />
			</ShowOrHideOnBreakpoint>

			{!!template && props.isInHeaderContext && (
				<ParentProvider value={null}>
					<div className={cn('mega-menu', animationClasses)}>{template}</div>
				</ParentProvider>
			)}

			{!!children && (!props.isInHeaderContext || !template) && (
				<ShowOrHideOnBreakpoint query=">=xx-large">
					<NavigationItemList
						level="sub"
						classNames={cn({active: isActive}, {'has-active': hasActive}, animationClasses)}
					>
						{children}
					</NavigationItemList>
				</ShowOrHideOnBreakpoint>
			)}
		</li>
	);
};

export default NavigationItemContent;
